import React from "react";
import { type RefCallBack } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { Label } from "@components/Label/Label";

type TValue = string | number;
type TLabel = string | number;

type TCheckboxGroupOption = {
  /**
   * Option value.
   */
  value: TValue;
  /**
   * Option label.
   */
  label: TLabel;
  /**
   * Disabled option flag.
   */
  disabled?: boolean;
};

export interface ICheckboxGroupProps {
  /**
   * Select field name.
   */
  name?: string;
  /**
   * Available options array.
   */
  options: TCheckboxGroupOption[];
  /**
   * Selected option value.
   */
  value?: TValue[];
  /**
   * Select field label.
   */
  label?: string | JSX.Element;
  /**
   * Value change callback.
   */
  onChange?: (values?: TValue[]) => void;
  /**
   * Disabled field flag.
   */
  disabled?: boolean;
  /**
   * Field error text.
   */
  error?: string;
  /**
   * Field hint text.
   */
  hint?: string;
  /**
   * Select field classname.
   */
  className?: string;
  /**
   * Ref.
   */
  inputRef?: RefCallBack;
}

export const CheckboxGroup: React.FC<ICheckboxGroupProps> = ({
  options,
  value = [],
  name,
  label,
  onChange,
  disabled,
  error,
  hint,
  className,
  inputRef,
}) => {
  const handleChange = (selectedOption: TValue) => {
    const newArray = [...value];
    const item = selectedOption;

    if (newArray.length > 0) {
      const index = newArray.findIndex((x) => x === item);

      if (index === -1) {
        newArray.push(item);
      } else {
        newArray.splice(index, 1);
      }
    } else {
      newArray.push(item);
    }

    onChange?.(newArray);
  };

  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {label && <Label>{label}</Label>}
      <div className="flex flex-col gap-2">
        {options.map((option, index) => (
          <Checkbox
            ref={inputRef}
            key={index}
            name={name}
            checked={value.includes(option.value)}
            label={option.label}
            disabled={option.disabled ?? disabled}
            onChange={() => {
              handleChange(option.value);
            }}
          />
        ))}
      </div>
      {hint && !error && <span className="text-sm text-grey-dark">{hint}</span>}
      {error && (
        <span className="break-all text-sm text-red-dark">{error}</span>
      )}
    </div>
  );
};
